import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog";
import { Button } from "@/modules/shared/ui/components/button";
import { Input } from "@/modules/shared/ui/components/input";
import { Label } from "@/modules/shared/ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select";
import {
  useSageCompanies,
  useSageCompanyRules,
} from "../../hooks/useSalaryIncreaseLookups";
import {
  INCREASE_POPULATION_LABELS,
  IncreasePopulation,
} from "../../enums/increase-population";

const MAX_APPROVED_PERCENTAGE = 15;
const ISO_DATE_LENGTH = 10;

export interface OpenPeriodFormValues {
  population: IncreasePopulation;
  approvedPercentage: number;
  effectiveDate: string;
  approvedBy: string;
  companyCode: string;
  companyRuleCodes: string[];
}

interface OpenPeriodDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting?: boolean;
  onSubmit?: (values: OpenPeriodFormValues) => void;
}

const randFormatter = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
  maximumFractionDigits: 0,
});

const countFormatter = new Intl.NumberFormat("en-ZA");

// Step 1 of the Servest workflow: capture the Remco-approved percentage,
// effective date, company and the business units in scope. Business units are
// the Sage 300 company rule codes of the selected company.
export function OpenPeriodDialog({
  open,
  onOpenChange,
  isSubmitting = false,
  onSubmit,
}: OpenPeriodDialogProps) {
  const [population, setPopulation] = useState<IncreasePopulation>(
    IncreasePopulation.Ctc,
  );
  const [percentage, setPercentage] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [selectedRuleCodes, setSelectedRuleCodes] = useState<string[]>([]);

  const companies = useSageCompanies(open);
  const rules = useSageCompanyRules(companyCode || undefined);

  const pctValue = Number(percentage) / 100;

  const selectedBaseline = (rules.data ?? [])
    .filter((rule) => selectedRuleCodes.includes(rule.companyRuleCode))
    .reduce((sum, rule) => sum + rule.totalGrossSalary, 0);
  const pool = Math.round(selectedBaseline * (pctValue || 0));

  const handleCompanyChange = (code: string) => {
    setCompanyCode(code);
    setSelectedRuleCodes([]);
  };

  const toggleRule = (ruleCode: string) =>
    setSelectedRuleCodes((prev) =>
      prev.includes(ruleCode)
        ? prev.filter((x) => x !== ruleCode)
        : [...prev, ruleCode],
    );

  const canSubmit =
    pctValue > 0 &&
    pctValue < MAX_APPROVED_PERCENTAGE / 100 &&
    companyCode !== "" &&
    selectedRuleCodes.length > 0 &&
    effectiveDate.length === ISO_DATE_LENGTH;

  const handleSubmit = () =>
    onSubmit?.({
      population,
      approvedPercentage: pctValue,
      effectiveDate,
      approvedBy,
      companyCode,
      companyRuleCodes: selectedRuleCodes,
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Open Increase Period</DialogTitle>
          <DialogDescription>
            Capture the Remco-approved figure with a timestamped record.
            Employees, salaries and reporting lines are pulled live from Sage
            300 People.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2 sm:grid-cols-2">
          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="company">Company</Label>
            <Select
              value={companyCode}
              onValueChange={handleCompanyChange}
              disabled={companies.isLoading}
            >
              <SelectTrigger id="company" className="w-full">
                <SelectValue
                  placeholder={
                    companies.isLoading
                      ? "Loading companies…"
                      : "Select a company"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {companies.data?.map((company) => (
                  <SelectItem
                    key={company.companyCode}
                    value={company.companyCode}
                  >
                    {company.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {companies.isError && (
              <p className="text-[11px] text-destructive">
                Could not load companies from Sage 300.
              </p>
            )}
          </div>

          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="population">Employee population</Label>
            <Select
              value={population}
              onValueChange={(value) =>
                setPopulation(value as IncreasePopulation)
              }
            >
              <SelectTrigger id="population" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(IncreasePopulation).map((value) => (
                  <SelectItem key={value} value={value}>
                    {INCREASE_POPULATION_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {population === IncreasePopulation.Wage && (
              <p className="text-[11px] text-muted-foreground">
                Wage adjustments are governed by the Minimum Wage Act and
                sectoral bargaining councils. They are tracked here and
                allocated outside this workflow.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="pct">Remco-approved %</Label>
            <div className="relative">
              <Input
                id="pct"
                type="number"
                step="0.05"
                min="0"
                max={MAX_APPROVED_PERCENTAGE}
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                className="pr-8"
              />
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                %
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="eff">Effective date</Label>
            <Input
              id="eff"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </div>

          <div className="sm:col-span-2 space-y-1.5">
            <Label htmlFor="approved-by">Approval record</Label>
            <Input
              id="approved-by"
              value={approvedBy}
              onChange={(e) => setApprovedBy(e.target.value)}
              placeholder="e.g. Remco meeting 14 Nov 2025"
            />
          </div>

          <div className="sm:col-span-2 space-y-2">
            <Label>Business units in scope</Label>
            <div className="grid gap-1.5 rounded-lg border border-border bg-muted/30 p-2 sm:grid-cols-2">
              {!companyCode && (
                <p className="px-1 py-1.5 text-xs text-muted-foreground sm:col-span-2">
                  Select a company to see its business units.
                </p>
              )}
              {companyCode && rules.isLoading && (
                <p className="px-1 py-1.5 text-xs text-muted-foreground sm:col-span-2">
                  Loading business units…
                </p>
              )}
              {companyCode && rules.isError && (
                <p className="px-1 py-1.5 text-xs text-destructive sm:col-span-2">
                  Could not load business units from Sage 300.
                </p>
              )}
              {companyCode &&
                rules.isSuccess &&
                rules.data.length === 0 && (
                  <p className="px-1 py-1.5 text-xs text-muted-foreground sm:col-span-2">
                    No business units available for this company.
                  </p>
                )}
              {rules.data?.map((rule) => {
                const on = selectedRuleCodes.includes(rule.companyRuleCode);
                return (
                  <button
                    key={rule.companyRuleCode}
                    type="button"
                    onClick={() => toggleRule(rule.companyRuleCode)}
                    className={[
                      "flex items-center justify-between rounded-md border px-2.5 py-1.5 text-xs transition-colors",
                      on
                        ? "border-primary/40 bg-primary/10 text-foreground"
                        : "border-transparent bg-card text-muted-foreground hover:bg-card",
                    ].join(" ")}
                  >
                    <span className="flex flex-col items-start">
                      <span className="flex items-center gap-1.5">
                        {on && (
                          <CheckCircle2 className="size-3.5 text-primary" />
                        )}
                        {rule.companyRuleCode}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {countFormatter.format(rule.employeeCount)}{" "}
                        {rule.employeeCount === 1 ? "employee" : "employees"}
                      </span>
                    </span>
                    <span className="tabular-nums">
                      {randFormatter.format(rule.totalGrossSalary)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="sm:col-span-2 rounded-lg border border-border bg-card p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Derived monthly budget pool
              </span>
              <span className="text-lg font-semibold tabular-nums">
                {randFormatter.format(pool)}
              </span>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Sum of selected BU baselines × approved %. Recalculated live from
              Sage 300 People.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Opening…" : "Open period"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
